import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Trash2,
  Edit,
  Plus,
  X,
  Eye,
  Search,
  ChevronDown,
  Loader,
  ChevronUp,
} from "lucide-react";
import adminService from "../../../services/api/admin.service"; // Import the service (adjust path as needed)

const CategoriesManagement = () => {
  // States
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "order",
    direction: "asc",
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    featuredImage: { url: "", alt: "" },
    metadata: { seoTitle: "", seoDescription: "" },
    order: "",
    status: "active",
  });

  const [formSubmitting, setFormSubmitting] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch categories using service
  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getAllCategories();
      setCategories(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error("Failed to load categories: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Reset form and cancel edit mode
  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      featuredImage: { url: "", alt: "" },
      metadata: { seoTitle: "", seoDescription: "" },
      order: "",
      status: "active",
    });
    setEditMode(false);
    setSelectedCategory(null);
    setShowForm(false);
  };

  // Set form to edit mode with category data
  const handleEdit = (category) => {
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || "",
      featuredImage: category.featuredImage || { url: "", alt: "" },
      metadata: category.metadata || { seoTitle: "", seoDescription: "" },
      order: category.order || "",
      status: category.status || "active",
    });
    setSelectedCategory(category);
    setEditMode(true);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle view category details
  const handleViewDetails = (categoryId) => {
    try {
      if (!categoryId) {
        throw new Error("Invalid category ID");
      }

      setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    } catch (error) {
      console.error("Error in handleViewDetails:", error.message);
      toast.error("Something went wrong while toggling category details.");
    }
  };

  // Show delete confirmation
  const confirmDelete = (categoryId) => {
    setDeleteConfirm(categoryId);
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  // Handle category deletion using service
  const handleDelete = async (categoryId) => {
    try {
      await adminService.deleteCategory(categoryId);
      toast.success("Category deleted successfully");
      fetchCategories();
    } catch (err) {
      toast.error("Error deleting category: " + err.message);
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Create/Update category form submit handler using service
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    if (!formData.name || !formData.slug) {
      toast.error("Name and Slug are required!");
      setFormSubmitting(false);
      return;
    }

    try {
      if (editMode && selectedCategory) {
        // Update existing category
        await adminService.updateCategory(selectedCategory._id, formData);
        toast.success("Category updated successfully!");
      } else {
        // Create new category
        const newCategory = await adminService.createCategory(formData);
        setCategories((prev) => [...prev, newCategory]); // Add the new category to state
        toast.success("Category created successfully!");
      }
      resetForm();
      fetchCategories(); // Still keep this to ensure data is fresh
    } catch (err) {
      toast.error(err.message || "Operation failed. Try again.");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle search functionality
  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description &&
        category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle sorting
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to categories
  const sortedCategories = [...filteredCategories].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Generate sorting indicator for table headers
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ChevronUp size={16} />
    ) : (
      <ChevronDown size={16} />
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Categories Management
        </h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          {showForm ? <X size={20} /> : <Plus size={20} />}
          {showForm ? "Close Form" : "Add Category"}
        </button>
      </div>

      {error && (
        <div
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6"
          role="alert"
        >
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}

      {/* Category Form Section */}
      {showForm && (
        <div className="bg-white shadow-lg rounded-xl p-6 mb-8 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2">
            {editMode ? "Edit Category" : "Create New Category"}
          </h2>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition"
                  placeholder="Category name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition"
                  placeholder="category-slug"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition"
                  placeholder="Optional description"
                  rows="3"
                ></textarea>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Featured Image URL
                </label>
                <input
                  type="text"
                  name="featuredImage.url"
                  value={formData.featuredImage.url}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      featuredImage: {
                        ...prev.featuredImage,
                        url: e.target.value,
                      },
                    }));
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  SEO Title
                </label>
                <input
                  type="text"
                  name="metadata.seoTitle"
                  value={formData.metadata.seoTitle}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      metadata: { ...prev.metadata, seoTitle: e.target.value },
                    }));
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition"
                  placeholder="SEO Title (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">
                    Order
                  </label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 flex justify-end gap-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={formSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formSubmitting
                  ? editMode
                    ? "Updating..."
                    : "Creating..."
                  : editMode
                  ? "Update Category"
                  : "Create Category"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories List Section - Rest of the component remains unchanged */}
      <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
        {/* Table code remains the same */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-800">
              Categories List
            </h2>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search categories..."
                className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-64 focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none"
              />
              <Search
                className="absolute left-3 top-2.5 text-gray-400"
                size={18}
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center p-12">
            <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
          </div>
        ) : sortedCategories.length === 0 ? (
          <div className="text-center p-12 text-gray-500">
            {searchTerm
              ? "No categories match your search"
              : "No categories found"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort("name")}
                  >
                    <div className="flex items-center">
                      Name {getSortIndicator("name")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort("slug")}
                  >
                    <div className="flex items-center">
                      Slug {getSortIndicator("slug")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort("status")}
                  >
                    <div className="flex items-center">
                      Status {getSortIndicator("status")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort("order")}
                  >
                    <div className="flex items-center">
                      Order {getSortIndicator("order")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedCategories.map((category) => (
                  <React.Fragment key={category._id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {category.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {category.slug}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            category.status === "active"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {category.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.order || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleViewDetails(category._id)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-100"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => confirmDelete(category._id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded details row */}
                    {expandedCategory === category._id && (
                      <tr className="bg-gray-50">
                        <td colSpan="5" className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-2">
                                Description:
                              </h4>
                              <p className="text-gray-600">
                                {category.description ||
                                  "No description provided"}
                              </p>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-2">
                                Metadata:
                              </h4>
                              <p className="text-gray-600">
                                {typeof category.metadata === "object"
                                  ? category.metadata.seoTitle ||
                                    "No SEO title provided"
                                  : category.metadata || "No metadata provided"}
                              </p>
                            </div>
                            {category.featuredImage && (
                              <div className="md:col-span-2">
                                <h4 className="font-semibold text-gray-700 mb-2">
                                  Featured Image:
                                </h4>
                                <div className="mt-2 max-w-xs h-32 rounded-lg overflow-hidden bg-gray-100">
                                  <img
  src={
    typeof category.featuredImage === "object"
      ? category.featuredImage.url
      : category.featuredImage
  }
  alt={
    typeof category.featuredImage === "object"
      ? category.featuredImage.alt || category.name
      : category.name
  }
  className="w-full h-full object-cover"
  onError={(e) => {
    e.target.onerror = null; // Prevent infinite loop if fallback also fails
    e.target.src = "https://archive.org/download/placeholder-image/placeholder-image.jpg";
  }}
/>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Delete confirmation row */}
                    {deleteConfirm === category._id && (
                      <tr className="bg-red-50">
                        <td colSpan="5" className="px-6 py-4">
                          <div className="flex flex-col md:flex-row justify-between items-center">
                            <p className="font-medium text-red-600 mb-4 md:mb-0">
                              Are you sure you want to delete "{category.name}"?
                              This action cannot be undone.
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={cancelDelete}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleDelete(category._id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesManagement;
